import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/errors";
import api from "@/lib/api";
import { Zap, Loader2, ScanLine, Nfc, Mic, ArrowLeft, LogIn, Lock, Eye, EyeOff, ExternalLink, Sparkles, ArrowRight, Hammer, Wallet } from "lucide-react";
import { toast } from "sonner";

const PRICING_URL = "https://www.kallitag.fr/tarifs";
const SIGNUP_URL = "https://www.kallitag.fr/inscription";
const FORGOT_URL = "https://www.kallitag.fr";

const PLANS = [
  { slug: "solo", name: "Solo", desc: "1 utilisateur · capture, débrief IA, export PDF & relance email", price: "24,90 €", unit: "/mois" },
  { slug: "equipe", name: "Équipe", desc: "2 à 9 licences · dashboard manager + Sync CRM 1-clic", price: "21,90 €", unit: "/licence/mois", popular: true },
  { slug: "entreprise", name: "Entreprise", desc: "10 licences et + · archivage, comparateur & synthèse IA", price: "19,90 €", unit: "/licence/mois" },
];

function PricingBlock({ label }) {
  return (
    <div className="mt-8" data-testid="subscribe-section">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap">{label}</span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <div className="mt-4 space-y-2">
        {PLANS.map((p) => (
          <a
            key={p.slug}
            href={`${PRICING_URL}?plan=${p.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`plan-${p.slug}`}
            className={`group flex items-center justify-between rounded-xl border p-3 transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer ${p.popular ? "border-primary bg-primary/5 hover:bg-primary/10" : "border-border hover:border-primary/50"}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{p.name}</span>
                {p.popular && <span className="text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5">Populaire</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">{p.desc}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0 pl-3">
              <div className="text-right">
                <div className="text-base font-extrabold text-foreground">{p.price}</div>
                <div className="text-[10px] text-muted-foreground">{p.unit}</div>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </div>
          </a>
        ))}
      </div>
      <div className="mt-4 flex flex-col items-center gap-1.5" data-testid="trial-badge">
        <span className="text-xs font-semibold text-primary bg-primary/10 rounded-full px-3 py-1">Essai gratuit 7 jours · sans carte bancaire</span>
        <span className="text-[11px] text-muted-foreground">Tarif dégressif : le prix par licence baisse dès 2 licences.</span>
      </div>
      <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" data-testid="signup-cta" className="block mt-3">
        <Button className="w-full h-11 rounded-xl text-base font-semibold">
          Créer un compte gratuit <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </a>
      <a href={PRICING_URL} target="_blank" rel="noopener noreferrer" data-testid="subscribe-cta" className="block mt-2">
        <Button variant="outline" className="w-full h-11 rounded-xl text-base font-semibold">
          Voir les offres &amp; s'abonner <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </a>
      <p className="text-[11px] text-muted-foreground text-center mt-2">Aucune carte bancaire pour l'essai · Résiliable en 1 clic</p>
    </div>
  );
}

export default function LoginPage() {
  const { login, register, loginKallitag, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // "form" | "blocked"
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState(() => localStorage.getItem("klt_last_email") || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("register") === "1" || p.get("mode") === "register") setMode("register");
  }, []);

  const finish = () => {
    localStorage.setItem("klt_last_email", email.trim().toLowerCase());
    toast.success("Bienvenue sur Kallitag");
    navigate("/");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    if (mode === "register" && password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "register") {
        await register({ email, password, name, company, remember });
      } else {
        await login(email, password, remember);
      }
      finish();
    } catch (err) {
      if (err.response?.status === 403) {
        setStep("blocked");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitKallitag = async () => {
    if (!email || !password) { toast.error("Renseignez votre email et mot de passe Kallitag."); return; }
    setLoading(true);
    try {
      await loginKallitag(email, password, remember);
      finish();
    } catch (err) {
      if (err.response?.status === 403) {
        setStep("blocked");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const forgot = async () => {
    if (!email) { toast.error("Entrez votre email pour recevoir le lien de réinitialisation."); return; }
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      toast.success("Si un compte existe pour cet email, un lien de réinitialisation vient d'être envoyé.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="klt-app min-h-screen flex flex-col lg:flex-row bg-background">
      {/* Brand panel */}
      <div className="lg:w-1/2 relative overflow-hidden bg-[#0B0F17] text-white p-8 sm:p-12 flex flex-col justify-between">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#A855F7]/25 blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-96 h-96 rounded-full bg-[#10B981]/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl klt-fab-neon flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" fill="currentColor" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-xl">Kallitag</div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Terrain</div>
          </div>
        </div>

        <div className="relative klt-fade-up">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
            Dictez votre chantier.<br />
            <span className="klt-neon-text" style={{ color: "#34F5B5", textShadow: "0 0 24px rgba(16,185,129,.5)" }}>L'appli fait le reste.</span>
          </h1>
          <p className="mt-4 text-slate-300 max-w-md text-base leading-relaxed">
            Client, devis, planning et trésorerie créés en une seule dictée. L'outil terrain des artisans et commerciaux.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {[
              { icon: Mic, label: "Dictée IA" },
              { icon: Hammer, label: "Devis & Chantiers" },
              { icon: Wallet, label: "Trésorerie" },
            ].map((f) => (
              <div key={f.label} className="rounded-2xl bg-[#161C28] p-4">
                <f.icon className="w-6 h-6 text-[#34F5B5] mb-2" />
                <div className="text-xs font-medium text-slate-200">{f.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© 2026 Kallitag — Écosystème NFC B2B</div>
      </div>

      {/* Form panel */}
      <div className="lg:w-1/2 flex items-center justify-center p-6 sm:p-12 pb-28 sm:pb-12">
        <div className="w-full max-w-md klt-fade-up">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            {step !== "form" ? "Abonnement requis" : mode === "register" ? "Créer un compte" : "Connexion"}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {step !== "form"
              ? "Votre compte Kallitag n'a pas d'abonnement Lead Capture actif."
              : mode === "register"
                ? "Créez votre compte Lead Capture — essai gratuit de 7 jours, sans carte bancaire."
                : "Connectez-vous à votre compte Lead Capture."}
          </p>

          {step === "form" && (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
              {mode === "register" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Nom complet</Label>
                    <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Prénom Nom" required />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Entreprise <span className="text-muted-foreground">(optionnel)</span></Label>
                    <Input data-testid="register-company-input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Nom de votre entreprise" />
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  data-testid="login-email-input"
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@entreprise.com"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Mot de passe</Label>
                <div className="relative">
                  <Input
                    data-testid="login-password-input"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    data-testid="toggle-password-btn"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPwd ? "Masquer" : "Afficher"}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="remember-me-checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 rounded border-input accent-primary cursor-pointer"
                  />
                  Rester connecté
                </label>
                <button type="button" onClick={forgot} data-testid="forgot-password-link" className="text-sm text-primary font-medium hover:underline">
                  Mot de passe oublié ?
                </button>
              </div>

              <Button type="submit" disabled={loading || !email || !password || (mode === "register" && !name)} data-testid={mode === "register" ? "register-submit-btn" : "login-submit-btn"} className="w-full h-11 rounded-xl text-base font-semibold">
                {loading ? <Loader2 className="w-4 h-4 klt-spin" /> : mode === "register" ? <><Sparkles className="w-4 h-4 mr-2" /> Créer mon compte · Essai 7 j</> : <><LogIn className="w-4 h-4 mr-2" /> Se connecter</>}
              </Button>

              <button type="button" onClick={() => setMode(mode === "register" ? "login" : "register")} data-testid="toggle-auth-mode-btn" className="w-full text-sm text-primary font-medium hover:underline">
                {mode === "register" ? "J'ai déjà un compte — Se connecter" : "Nouveau ? Créer un compte gratuit (essai 7 j, sans CB)"}
              </button>

              <div className="flex items-center gap-3 pt-1">
                <div className="h-px flex-1 bg-border" />
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">ou</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <Button type="button" variant="outline" onClick={submitKallitag} disabled={loading || !email || !password} data-testid="kallitag-login-btn" className="w-full h-11 rounded-xl text-base font-semibold">
                <Zap className="w-4 h-4 mr-2" fill="currentColor" /> Se connecter avec Kallitag
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">Clients kallitag.fr existants : utilisez ce bouton.</p>
            </form>
          )}

          {step === "form" && mode === "login" && <PricingBlock label="Nos offres" />}

          <a href="/decouvrir" data-testid="footer-landing-link" className="block mt-6">
            <div className="group relative overflow-hidden rounded-xl border border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </span>
                <div className="leading-tight">
                  <div className="text-sm font-semibold text-foreground">Découvrir l'app</div>
                  <div className="text-[11px] text-muted-foreground">Fonctionnalités, offres & essai 7 j</div>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-primary transition-transform group-hover:translate-x-1" />
            </div>
          </a>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
            <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-signup-link" className="hover:text-foreground">Créer un compte</a>
            <span>·</span>
            <a href="/confidentialite" data-testid="footer-privacy-link" className="hover:text-foreground">Confidentialité</a>
            <span>·</span>
            <a href="/mentions-legales" data-testid="footer-legal-link" className="hover:text-foreground">Mentions légales</a>
            <span>·</span>
            <a href="/cgu" data-testid="footer-cgu-link" className="hover:text-foreground">CGU / CGV</a>
            <span>·</span>
            <a href="/dpa" data-testid="footer-dpa-link" className="hover:text-foreground">DPA</a>
          </div>

          {step === "blocked" && (
            <div className="mt-8 space-y-5" data-testid="subscription-required">
              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
                <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-amber-600" />
                </div>
                <p className="mt-3 text-sm text-foreground leading-relaxed">
                  Votre identité <span className="font-medium">{email}</span> est reconnue, mais l'accès à
                  Lead Capture nécessite un abonnement actif. Souscrivez sur kallitag.fr, puis reconnectez-vous.
                </p>
              </div>
              <PricingBlock label="Nos offres" />
              <button type="button" onClick={() => { setStep("form"); setPassword(""); }} data-testid="back-to-login-btn" className="w-full text-sm text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Utiliser un autre compte
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
